/**
 * Resizable Option — v3 (May 2026)
 *
 * Changes from v2:
 * - Removed the dead `this.engraving = document.querySelector('#engravingProduct')`
 *   reference (the unified EngravingManager doesn't expose that ID).
 * - `updateVariantInput()` now runs at init() time, so the resizing
 *   service is ALWAYS at items[2] regardless of what the HTML name
 *   attributes say. You no longer need to edit the HTML inputs.
 *
 * Final slot layout (hardcoded — no shifts during the user flow):
 *   items[0] = main product (single ring or bundle)
 *   items[1] = engraving service (Engrave It or Engrave Them, when present)
 *   items[2] = resizing service (Custom Resizing, when present)
 */
if (!window.customElements.get('resizable-option')) {
  window.customElements.define('resizable-option', class ResizableOption extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.id;
      window.resizableOptions = window.resizableOptions || {};
    }

    connectedCallback() {
      window.resizableOptions.needToAddVariant = false;
      // CRITICAL: lock the resizing inputs to items[2] IMMEDIATELY so they
      // can't collide with the engraving service at items[1] if the user
      // submits the form before the deferred init() runs. (The 1s delay
      // below is for variant data/Alpine to settle, but slot assignment
      // is just a DOM rename — safe to do right away.)
      this.lockResizingSlot();
      setTimeout(() => { this.init(); }, 1000);
    }

    lockResizingSlot() {
      const variantInput = document.querySelector('[data-resizing-variant-input]');
      const feeQty = this.querySelector('[data-resizing-qty-input]');
      const feeName = this.querySelector('[data-res-fee-name]');
      const feeBase = this.querySelector('[data-res-fee-base]');
      const feeNew = this.querySelector('[data-res-fee-new]');
      const feePid = this.querySelector('[data-res-fee-pid]');

      if (variantInput) variantInput.setAttribute('name', 'items[2]id');
      if (feeQty) feeQty.setAttribute('name', 'items[2]quantity');
      if (feeName) feeName.setAttribute('name', 'items[2]properties[Resizing For]');
      if (feeBase) feeBase.setAttribute('name', 'items[2]properties[Original Size]');
      if (feeNew) feeNew.setAttribute('name', 'items[2]properties[New Size]');
      if (feePid) feePid.setAttribute('name', 'items[2]properties[_purchase_id]');
    }

    init() {
      this.sizeOptions = document.querySelectorAll('[data-single-option-selector]');
      this.input = document.querySelector('[data-resizable-property-input]');
      this.helperText = document.querySelector('.resizable-option__helper--text');
      this.allOptions = document.querySelectorAll('[data-resizable-option]');
      this.options = document.querySelectorAll('[data-resizable-option]');
      this.productForm = document.querySelector(`#product-form-${this.sectionId}`);
      this.addToCartButton = this.productForm?.querySelector('button[type="submit"]');
      this.variantInput = document.querySelector('[data-resizing-variant-input]');
      this.baseVariantInput = this.querySelector('[data-base-variant-resizing-input]');

      this.feeQty = this.querySelector('[data-resizing-qty-input]');
      this.feeName = this.querySelector('[data-res-fee-name]');
      this.feeBase = this.querySelector('[data-res-fee-base]');
      this.feeNew = this.querySelector('[data-res-fee-new]');
      this.feePid = this.querySelector('[data-res-fee-pid]');
      this.combinedAttrInput = this.querySelector('[data-res-combined-attr]');

      const productVariantsJson = this.querySelector('[data-product-variants]');
      if (productVariantsJson) this.variants = JSON.parse(productVariantsJson.textContent);

      // Force resizing inputs into items[2] (engraving service lives in items[1]).
      this.updateVariantInput();

      this.options?.forEach((option) => {
        option.addEventListener('click', () => { this.handleOptionSelection(option); });
      });
      this.sizeOptions?.forEach((option) => {
        option.addEventListener('click', () => { this.handleOptionSelection(null); });
      });
      this.productForm?.addEventListener('change', () => { this.updateAvailability(); });

      if (this.productForm && !this.productForm.hasAttribute('data-resizable-bound')) {
        this.productForm.setAttribute('data-resizable-bound', 'true');
        this.addToCartButton?.addEventListener('click', (event) => {
          // this.handleFormSubmission(event, true, this.addToCartButton);
        });
      }
      this.updateAvailability();
    }

    updateVariantInput() {
      if (this.variantInput) this.variantInput.setAttribute('name', 'items[2]id');
      if (this.feeQty) this.feeQty.setAttribute('name', 'items[2]quantity');
      if (this.feeName) this.feeName.setAttribute('name', 'items[2]properties[Resizing For]');
      if (this.feeBase) this.feeBase.setAttribute('name', 'items[2]properties[Original Size]');
      if (this.feeNew) this.feeNew.setAttribute('name', 'items[2]properties[New Size]');
      if (this.feePid) this.feePid.setAttribute('name', 'items[2]properties[_purchase_id]');
    }

    handleFormSubmission(evt, blockEvent, button) {
      if (!window.resizableOptions.needToAddVariant || !this.variantInput) return;
      if (blockEvent) evt.preventDefault();
      this.variantId = this.variantInput.value;
      fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }] })
      })
        .then((response) => response.json())
        .then(() => {
          window.resizableOptions.needToAddVariant = false;
          if (blockEvent) button.click();
        })
        .catch((error) => { console.error('Error:', error); });
    }

    updateAvailability() {
      if (!this.variants) return;
      const checkedInputs = Array.from(document.querySelectorAll('[data-single-option-selector]:checked'));

      this.allOptions.forEach(resizingOption => {
        const parentId = resizingOption.dataset.parentId;
        const parentInput = document.getElementById(parentId);
        if (parentInput) {
          const optionIndex = parentInput.dataset.index;
          const baseValue = parentInput.value;
          let isAvailable = false;

          const matchingVariant = this.variants.find(variant => {
            let match = true;
            checkedInputs.forEach(checkedInput => {
              if (checkedInput.dataset.index === optionIndex) {
                if (variant[checkedInput.dataset.index] !== baseValue) match = false;
              } else {
                if (variant[checkedInput.dataset.index] !== checkedInput.value) match = false;
              }
            });
            return match;
          });

          if (matchingVariant && matchingVariant.available) isAvailable = true;
          if (!isAvailable) {
            resizingOption.classList.add('out-of-stock');
            if (resizingOption.classList.contains('active')) {
              resizingOption.classList.remove('active');
              this.handleOptionSelection(null);
            }
          } else {
            resizingOption.classList.remove('out-of-stock');
          }
        }
      });
    }

    handleOptionSelection(option) {
      if (option && option.classList.contains('out-of-stock')) return;
      this.allOptions.forEach(opt => opt.classList.remove('active'));
      const blockContainer = this.closest('.product-variant-picker-block');

      if (option) {
        option.classList.add('active');
        blockContainer?.classList.add('resizing-active');
        const parentId = option.dataset.parentId;
        const parentInput = document.getElementById(parentId);

        if (parentInput) {
          parentInput.checked = true;
          const optionIndex = parentInput.dataset.index;

          // Direct variant ID sync to bypass Alpine.js async timing —
          // if the customer clicks Add to Cart before Alpine finishes,
          // the form would submit with the wrong variant otherwise.
          const formVariantInput = this.productForm?.querySelector('input[name="id"]');
          if (formVariantInput && this.variants) {
            const checkedInputs = Array.from(document.querySelectorAll('[data-single-option-selector]:checked'));
            const targetVariant = this.variants.find(variant =>
              checkedInputs.every(input => {
                const idx = input.dataset.index;
                return variant[idx] === (idx === optionIndex ? parentInput.value : input.value);
              })
            );
            if (targetVariant) formVariantInput.value = targetVariant.id;
          }

          const alpineComponent = parentInput.closest('[x-data]');
          if (alpineComponent && window.Alpine) {
            const data = window.Alpine.$data(alpineComponent);
            if (data) data[optionIndex] = parentInput.value;
          }
          parentInput.dispatchEvent(new Event('input', { bubbles: true }));
          parentInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.input) {
          window.resizableOptions.needToAddVariant = true;

          const detailsString = `Original Size: ${parentInput.value} -> New Size: ${option.dataset.value}`;
          this.input.value = detailsString;
          this.input.removeAttribute('disabled');
          this.helperText?.classList.add('active');
          this.variantInput?.removeAttribute('disabled');
          this.baseVariantInput?.removeAttribute('disabled');

          if (this.feeQty) this.feeQty.removeAttribute('disabled');
          if (this.feeName) this.feeName.removeAttribute('disabled');
          if (this.feeBase) { this.feeBase.value = parentInput.value; this.feeBase.removeAttribute('disabled'); }
          if (this.feeNew)  { this.feeNew.value  = option.dataset.value;  this.feeNew.removeAttribute('disabled'); }
          // _purchase_id is filled in by theme-events.js at submit time.
          if (this.feePid) this.feePid.removeAttribute('disabled');

          if (this.combinedAttrInput && parentInput) {
            this.combinedAttrInput.name = this.combinedAttrInput.dataset.baseName;
            this.combinedAttrInput.value = detailsString;
            this.combinedAttrInput.removeAttribute('disabled');
          }
        }
      } else {
        blockContainer?.classList.remove('resizing-active');
        window.resizableOptions.needToAddVariant = false;
        this.input?.setAttribute('disabled', 'disabled');
        this.helperText?.classList.remove('active');
        this.variantInput?.setAttribute('disabled', 'disabled');
        this.baseVariantInput?.setAttribute('disabled', 'disabled');

        if (this.feeQty) this.feeQty.setAttribute('disabled', 'disabled');
        if (this.feeName) this.feeName.setAttribute('disabled', 'disabled');
        if (this.feeBase) this.feeBase.setAttribute('disabled', 'disabled');
        if (this.feeNew) this.feeNew.setAttribute('disabled', 'disabled');
        if (this.feePid) this.feePid.setAttribute('disabled', 'disabled');
        if (this.combinedAttrInput) this.combinedAttrInput.setAttribute('disabled', 'disabled');
      }
    }
  });
}